import moment from 'moment-timezone';
import { Reservation } from 'models/reservation';
import { ProductSummaryShape } from 'lib/util/productSummaryShape';

export const getKeyVisualURL = (
  reservation: Reservation,
  productSummaries: ProductSummaryShape[]
) => {
  const product = productSummaries.find((product) => {
    return product.id === reservation.product_id;
  });

  if (product) {
    return product.imageURL;
  }

  return '';
};

export const getLimit = (reservation: Reservation, productSummaryShape: ProductSummaryShape) => {
  if ((reservation?.checkin_info?.checkin_records || []).length === 0) {
    return null;
  }

  if (productSummaryShape?.checkinExpirationDate?.relativeDate?.type === 'ETERNAL') {
    return null;
  }

  const checkedInTime = moment.tz(
    reservation.checkin_info.checkin_records[0].date_time_utc,
    reservation.start_timezone || 'UTC'
  );

  if (productSummaryShape?.instantExpiration) {
    return checkedInTime;
  }

  if (productSummaryShape?.checkinExpirationDate?.relativeDate?.type === 'HOUR') {
    const count = productSummaryShape?.checkinExpirationDate?.relativeDate?.count ?? 0;
    return checkedInTime.add(count, 'h');
  }

  if (productSummaryShape?.checkinExpirationDate?.relativeDate?.type === 'DAY') {
    const count = productSummaryShape?.checkinExpirationDate?.relativeDate?.count ?? 0;
    const parts = (
      productSummaryShape?.checkinExpirationDate?.relativeDate?.timeLocal ?? '00:00'
    ).split(':');
    let hour = 0;
    let minute = 0;
    if (parts.length > 1) {
      hour = Number(parts[0]);
      minute = Number(parts[0]);
    }
    return checkedInTime
      .add(count, 'd')
      .set({ hour: hour, minute: minute, second: 0, millisecond: 0 });
  }

  return null;
};

export const isExpired = (reservation: Reservation, productSummaryShape: ProductSummaryShape) => {
  if ((reservation?.checkin_info?.checkin_records || []).length === 0) {
    return false;
  }

  const now = moment();

  const limit = getLimit(reservation, productSummaryShape);

  if (!limit) {
    return false;
  }

  if (now.isAfter(limit)) {
    return true;
  }
  return false;
};

export const getStatus = (reservation: Reservation, productSummaryShape: ProductSummaryShape) => {
  if (reservation.checkin_info?.checkin_status === 'NOT_CHECKED_IN') {
    return 'not_started';
  } else if (
    reservation.checkin_info?.checkin_status === 'CHECKED_IN' ||
    reservation.checkin_info?.checkin_status === 'IN_PROGRESS'
  ) {
    if (isExpired(reservation, productSummaryShape)) {
      return 'expired';
    } else {
      return 'started';
    }
  }
  return 'unsupported';
};

export const isLocalhost = () => {
  if (window.location.hostname.match(/localhost/)) {
    return true;
  }
  return false;
};

export const getCheckinTimeText = (reservation: Reservation, stubKey: string): string => {
  if ((reservation?.checkin_info?.checkin_records || []).length === 0) {
    return '';
  }

  const record = (reservation?.checkin_info?.checkin_records || []).find((record) => {
    if (!stubKey) {
      return true;
    }
    return record.stub_key == stubKey;
  });

  if (!record) {
    return '';
  }

  return moment.tz(record.date_time_utc, reservation.start_timezone || 'UTC').format('lll');
};

export const getCheckinStubs = (
  reservation: Reservation,
  productSummaries: ProductSummaryShape[]
) => {
  const product = productSummaries.find((product) => {
    return product.id === reservation.product_id;
  });

  if (product) {
    return product.checkinStubs;
  }

  return [];
};

export const getCheckinStubDisplayText = (
  reservation: Reservation,
  stub: { key: string; text: string; options?: { key: string; text: string }[] }
): string => {
  const record = (reservation?.checkin_info?.checkin_records || []).find(
    (record) => record.stub_key === stub.key
  );

  const stubOption = (stub?.options ?? []).find((option) => option.key === record?.stub_option_key);

  return stubOption?.text || stub?.text || '';
};

export const isCancellableSetting = (
  reservation: Reservation,
  productSummaries: ProductSummaryShape[],
  settings: any
) => {
  const product = productSummaries.find((product) => {
    return product.id === reservation.product_id;
  });

  if (product && product.guestMyPageSettings?.shouldUseCustomSettings) {
    return product.guestMyPageSettings?.allowCancellingReservation ?? false;
  }

  return settings?.guest_my_page_settings?.allow_cancelling_reservation ?? false;
};

export const isFreePass = (reservation: Reservation) => {
  return Boolean(reservation?.free_start_date_time_from);
};

export const isCheckinedStub = (reservation: Reservation, stubKey: string): boolean => {
  if (
    (reservation?.checkin_info?.checkin_records || []).find((record) => record.stub_key === stubKey)
  ) {
    return true;
  }
  return false;
};
